import React, { useState, useEffect } from 'react';
import { Crown, Flag, Edit3, Check, X, Target, Shield, TrendingUp, Lightbulb, Users } from 'lucide-react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "./AxiosInstance.jsx";
import Boutton from "./Button.jsx";

const Accueil = ({ gameState, setGameState, refreshGameState }) => {
    const countries_dict = gameState?.countries_dict;
    const [selectedCountryKey, setSelectedCountryKey] = useState(null);
    const [selectedCountryName, setSelectedCountryName] = useState(null);

    const [leaderName, setLeaderName] = useState('');
    const [isEditingLeader, setIsEditingLeader] = useState(false);
    const [tempLeaderName, setTempLeaderName] = useState('');
    const [gamePhase, setGamePhase] = useState('selection');
    const [hoverCountry, setHoverCountry] = useState(null);

    const navigate = useNavigate();
    const countriesList = Object.entries(countries_dict || {});
    const selectedCountry = selectedCountryKey ? countries_dict[selectedCountryKey] : null;



    const initGameState = async () => {
        try {
            const response = await axios.get("/game_state", {
            });
            const initGame = response.data?.game_state;
            if (initGame) {
                setGameState(initGame);
            } else {
                console.warn("Réponse sans game_state valide", response.data);
            }

        } catch (error) {
            console.error("Erreur récupération état du jeu:", error.response?.data || error.message);
        }
    };

    const selectCountry = (key, country) => {
        setSelectedCountryKey(key);
        setSelectedCountryName(country.short_name);
        setLeaderName(`${country.leader}`);
        setGamePhase('leader');
    };

    const startEditingLeader = () => {
        setTempLeaderName(leaderName);
        setIsEditingLeader(true);
    };

    const confirmLeaderEdit = () => {
        if (tempLeaderName.trim()) {
            setLeaderName(tempLeaderName.trim());
        }
        setIsEditingLeader(false);
    };

    const cancelLeaderEdit = () => {
        setTempLeaderName('');
        setIsEditingLeader(false);
    };

    const readyToStart = () => {
        setGamePhase('ready');
    };

    const goGame = async () => {
        if (!selectedCountry) {
            alert("Veuillez sélectionner un pays avant de continuer.");
            return;
        }

        try {
            await axios.post("/go_game",
                { country: selectedCountry.short_name, leader: leaderName },
            );

            await new Promise(resolve => setTimeout(resolve, 1000));
            await refreshGameState();

        } catch (error) {
            console.error("Erreur lors de la requête go_game :", error.response?.data || error.message);
            alert("Impossible de lancer la partie. Veuillez réessayer.");
        }
    };



    const openConfig = () => {
        navigate("/login");
    };

    useEffect(() => {
        initGameState();
    }, []); // appel uniquement au premier rendu

    return (
        <div className="bg-black relative overflow-y-scroll">
            {/* Animated background */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute h-full inset-0 bg-gradient-to-br from-purple-900/20 via-blue-900/30 to-black" />

                {/* Grid pattern */}
                <div className="absolute inset-0 opacity-10">
                    <div className="h-full w-full" style={{
                        backgroundImage: `
              linear-gradient(rgba(59, 130, 246, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(59, 130, 246, 0.1) 1px, transparent 1px)
            `,
                        backgroundSize: '50px 50px'
                    }} />
                </div>

                {/* Floating orbs */}
                <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
                <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
                <div className="absolute top-1/2 left-1/2 w-32 h-32 bg-cyan-500/10 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '4s' }} />
            </div>

            {/* Neural network lines */}
            <svg className="absolute inset-0 w-full h-full opacity-5 pointer-events-none">
                <defs>
                    <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#60a5fa" />
                        <stop offset="100%" stopColor="#a855f7" />
                    </linearGradient>
                </defs>
                <path d="M100,200 Q400,100 800,300 T1200,400" stroke="url(#lineGradient)" strokeWidth="1" fill="none" />
                <path d="M200,500 Q600,400 1000,600 T1400,700" stroke="url(#lineGradient)" strokeWidth="1" fill="none" />
                <path d="M0,300 Q300,200 600,400 T1200,500" stroke="url(#lineGradient)" strokeWidth="1" fill="none" />
            </svg>

            {/* Main content */}
            <div className="relative z-10 min-h-screen flex flex-col">
                {/* Header */}
                <header className="flex justify-between items-center p-4 relative z-20">
                    <button onClick={() => navigate("/")} style={{ zIndex: 10 }}>
                        <img
                            src="/logoGeo.svg"
                            alt="GeoPolitikIA logo."
                            width={300}
                            height={64}
                        />
                    </button>
                    <div className="flex items-center gap-3 px-6 py-3">
                        <button
                            onClick={() => navigate('/multi')}
                            className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-400/50 hover:border-blue-400 text-blue-300 hover:text-white font-bold rounded-xl transition-all hover:shadow-lg hover:shadow-blue-500/20"
                        >
                            <Users className="w-5 h-5" />
                            MULTIJOUEUR
                        </button>
                        <Boutton
                            text="⚙️CONFIG "
                            onClick={openConfig}
                            text_after="⚙️LOGIN"
                            delay={0}
                            variant="grey"
                        />
                    </div>

                </header>

                <div>

                    {/* Main content area */}
                    <main className="flex-1 px-6 lg:px-8 pb-8 -my-16">
                        {gamePhase === 'selection' && (
                            <div className="max-w-7xl mx-auto">
                                {/* Hero section */}
                                <div className="text-center">
                                    <h2 className="text-4xl lg:text-6xl font-black mb-6 bg-gradient-to-r from-white via-blue-200 to-purple-200 bg-clip-text text-transparent leading-tight">
                                        L'Avenir Entre Vos Mains
                                    </h2>

                                    <div className="max-w-3xl mx-auto mb-8">
                                        <p className="text-lg lg:text-xl text-slate-300 leading-relaxed mb-6">
                                            Chaque décision forge l'histoire. Chaque alliance change le monde.
                                            Votre IA conseillée vous défiera à chaque tournant.
                                        </p>

                                        <div className="flex flex-wrap justify-center gap-6 text-sm">
                                            <div className="flex items-center space-x-2 text-slate-300">
                                                <Target className="w-4 h-4 text-red-400" />
                                                <span>Stratégie Globale</span>
                                            </div>
                                            <div className="flex items-center space-x-2 text-slate-300">
                                                <TrendingUp className="w-4 h-4 text-green-400" />
                                                <span>Économie Dynamique</span>
                                            </div>
                                            <div className="flex items-center space-x-2 text-slate-300">
                                                <Lightbulb className="w-4 h-4 text-yellow-400" />
                                                <span>IA Satirique</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="text-blue-400 font-mono text-sm animate-pulse mb-8">
                                        → Sélectionnez votre nation pour commencer
                                    </div>
                                </div>

                                {/* Countries selection */}
                                <div className="grid grid-cols-2 gap-4">
                                    {countriesList.map(([key, country]) => {
                                        const isSelected = key === selectedCountryKey;
                                        const isHovered = key === hoverCountry;

                                        // Couleurs de difficulté
                                        const difficultyColors = {
                                            'Facile': { bg: 'from-green-500/20 to-emerald-500/20', border: 'border-green-400', text: 'text-green-400' },
                                            'Moyenne': { bg: 'from-yellow-500/20 to-orange-500/20', border: 'border-yellow-400', text: 'text-yellow-400' },
                                            'Difficile': { bg: 'from-red-500/20 to-pink-500/20', border: 'border-red-400', text: 'text-red-400' }
                                        };
                                        const diffColor = difficultyColors[country.difficulty];
                                        return (
                                            <div
                                                key={key}
                                                onMouseEnter={() => setHoverCountry(key)}
                                                onMouseLeave={() => setHoverCountry(null)}
                                                onClick={() => selectCountry(key, country)}
                                                className={`group relative cursor-pointer transition-all duration-500 transform hover:scale-105 ${isSelected ? 'scale-105' : ''
                                                    }`}
                                            >
                                                <div className={`relative p-4 rounded-2xl border transition-all duration-300 backdrop-blur-sm ${isSelected
                                                    ? 'bg-gradient-to-br from-blue-500/20 to-purple-500/20 border-blue-400 shadow-2xl shadow-blue-500/25'
                                                    : 'bg-slate-900/40 border-slate-700/50 hover:border-slate-600 hover:bg-slate-800/50'
                                                    }`}>

                                                    {/* Difficulté badge */}
                                                    <div className={`absolute top-2 right-2 px-2 py-1 rounded-lg text-xs font-medium bg-gradient-to-r ${diffColor.bg} ${diffColor.border} border ${diffColor.text}`}>
                                                        {country.difficulty}
                                                    </div>

                                                    {/* Country header */}
                                                    <div className="flex items-center space-x-3 mb-3">
                                                        <div className={`w-16 h-16 rounded-xl bg-gradient-to-br flex items-center justify-center ${isSelected
                                                            ? 'from-blue-500 to-purple-600'
                                                            : 'from-slate-700 to-slate-800 group-hover:from-slate-600 group-hover:to-slate-700'
                                                            }`}>
                                                            <img
                                                                src={require(`../assets/flags/${country.short_name}.png`)}
                                                                alt={`Drapeau ${country.short_name}`}
                                                            />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <h3 className={`text-lg font-bold transition-colors truncate ${isSelected ? 'text-white' : 'text-slate-200 group-hover:text-white'
                                                                }`}>
                                                                {country.short_name}
                                                            </h3>
                                                            <div className={`text-xs font-mono truncate ${isSelected ? 'text-blue-300' : 'text-slate-500 group-hover:text-slate-400'
                                                                }`}>

                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Stats condensées */}
                                                    <div className="space-y-1.5 mb-3">
                                                        <div className="flex items-center justify-between text-xs">
                                                            <div className="flex items-center space-x-1">
                                                                <Shield className="w-3 h-3 text-red-400" />
                                                                <span className="text-slate-400">Population</span>
                                                            </div>
                                                            <div className="flex items-center space-x-1">
                                                                <div className="w-12 h-1 bg-slate-700 rounded-full overflow-hidden">
                                                                    <div className="h-full bg-red-400 transition-all duration-1000" style={{ width: `${country.population}%` }} />
                                                                </div>
                                                                <span className="text-slate-400 w-6 text-right">{country.population}</span>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center justify-between text-xs">
                                                            <div className="flex items-center space-x-1">
                                                                <TrendingUp className="w-3 h-3 text-green-400" />
                                                                <span className="text-slate-400">Ressources naturelles</span>
                                                            </div>
                                                            <div className="flex items-center space-x-1">
                                                                <div className="w-12 h-1 bg-slate-700 rounded-full overflow-hidden">
                                                                    <div className="h-full bg-green-400 transition-all duration-1000" style={{ width: `${country.natural_resources}%` }} />
                                                                </div>
                                                                <span className="text-slate-400 w-6 text-right">{country.natural_resources}</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Description courte */}
                                                    <p className="text-xs text-slate-400 leading-relaxed line-clamp-2">
                                                        {country.intro}
                                                    </p>

                                                    {/* Selection indicator */}
                                                    {isSelected && (
                                                        <div className="absolute top-2 left-2">
                                                            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center animate-pulse">
                                                                <Check className="w-4 h-4 text-white" />
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Hover glow effect */}
                                                    {(isHovered || isSelected) && (
                                                        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-2xl pointer-events-none" />
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {gamePhase === 'leader' && (
                            <div className="max-w-2xl mx-auto">
                                <div className="text-center mb-8">
                                    <div className="inline-block p-4 bg-slate-900/50 rounded-2xl border border-slate-700 mb-6">
                                        <img
                                            className="w-52 h-52"
                                            src={require(`../assets/flags/${selectedCountryName}.png`)}
                                            alt={`Drapeau ${selectedCountryName}`}
                                        />
                                        <h3 className="text-2xl font-bold text-white">{selectedCountryName}</h3>
                                        <p className="text-sm text-slate-400">Nation sélectionnée</p>
                                    </div>

                                    <h2 className="text-3xl lg:text-4xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                                        Personnalisez Votre Leader
                                    </h2>
                                    <p className="text-slate-300">L'histoire se souviendra de ce nom...</p>
                                </div>

                                <div className="bg-slate-900/50 backdrop-blur-sm rounded-3xl border border-slate-700 p-8">
                                    <label className="block text-sm font-medium text-slate-300 mb-4">
                                        Nom du Leader :
                                    </label>

                                    {isEditingLeader ? (
                                        <div className="flex items-center space-x-3 mb-6">
                                            <input
                                                type="text"
                                                value={tempLeaderName}
                                                onChange={(e) => setTempLeaderName(e.target.value)}
                                                className="flex-1 px-4 py-3 bg-slate-800 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20"
                                                placeholder="Entrez le nom de votre leader"
                                                autoFocus
                                                maxLength={40}
                                            />
                                            <button
                                                onClick={confirmLeaderEdit}
                                                className="p-3 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-xl transition-colors"
                                            >
                                                <Check className="w-5 h-5" />
                                            </button>
                                            <button
                                                onClick={cancelLeaderEdit}
                                                className="p-3 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-xl transition-colors"
                                            >
                                                <X className="w-5 h-5" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-between bg-slate-800/50 rounded-xl px-4 py-3 mb-6 border border-slate-700">
                                            <span className="text-xl font-medium text-white">{leaderName}</span>
                                            <button
                                                onClick={startEditingLeader}
                                                className="p-2 text-blue-400 hover:bg-blue-400/20 rounded-lg transition-colors"
                                            >
                                                <Edit3 className="w-5 h-5" />
                                            </button>
                                        </div>
                                    )}

                                    <div className="flex space-x-4">
                                        <button
                                            onClick={() => setGamePhase('selection')}
                                            className="flex-1 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-xl transition-colors"
                                        >
                                            Retour
                                        </button>
                                        <button
                                            onClick={readyToStart}
                                            disabled={isEditingLeader}
                                            className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-400 hover:to-purple-500 disabled:from-slate-600 disabled:to-slate-700 text-white font-bold rounded-xl transition-all disabled:cursor-not-allowed"
                                        >
                                            Continuer
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {gamePhase === 'ready' && (
                            <div className="max-w-3xl mx-auto text-center">
                                <div className="mb-8">
                                    <Crown className="w-16 h-16 text-yellow-400 mx-auto mb-4 animate-bounce" />
                                    <h2 className="text-4xl lg:text-5xl font-black mb-4 bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
                                        Prêt pour l'Histoire
                                    </h2>
                                </div>

                                <div className="bg-slate-900/50 backdrop-blur-sm rounded-3xl border border-slate-700 p-8 mb-8">
                                    <div className="flex items-center justify-center space-x-6 mb-6">
                                        <div className="text-center">
                                            <Flag className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                                            <div className="text-lg font-bold text-white">{selectedCountryName}</div>
                                            <div className="text-sm text-slate-400">Votre Nation</div>
                                        </div>
                                        <div className="w-px h-16 bg-slate-600" />
                                        <div className="text-center">
                                            <Crown className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                                            <div className="text-lg font-bold text-white">{leaderName}</div>
                                            <div className="text-sm text-slate-400">Votre Identité</div>
                                        </div>
                                    </div>

                                    <div className="text-slate-300 mb-6 leading-relaxed">
                                        L'avenir de <strong className="text-white">{selectedCountryName}</strong> repose désormais entre les mains de{' '}
                                        <strong className="text-blue-400">{leaderName}</strong>.
                                        Votre conseiller IA vous attend pour commencer cette aventure géopolitique...
                                    </div>

                                    <div className="flex space-x-4">
                                        <button
                                            onClick={() => setGamePhase('leader')}
                                            className="flex-1 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-xl transition-colors"
                                        >
                                            Modifier
                                        </button>
                                        <button
                                            onClick={goGame}
                                            className="flex-2 px-8 py-4 bg-gradient-to-r from-green-500 via-blue-500 to-purple-600 hover:from-green-400 hover:via-blue-400 hover:to-purple-500 text-white font-black text-lg rounded-xl transition-all transform hover:scale-105 shadow-lg hover:shadow-2xl"
                                        >
                                            🚀 LANCER LA SIMULATION
                                        </button>
                                    </div>
                                </div>

                                <div className="text-xs text-slate-500 font-mono">
                                    GeoPolitikIA v2.0.77 • Prêt pour le déploiement
                                </div>
                            </div>
                        )}
                    </main>
                </div>
            </div>
        </div>
    );
};

export default Accueil;
